import { ReactNode, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { OneDateNavigator, OneEmptyState, Spinner } from '@factorialco/f0-react/dist/experimental'
import { Cross } from '@factorialco/f0-react/icons/app'
import {
  OneOnOnesMeetingAttendeesDocument,
  OneOnOnesScheduledMeetingsDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { getDefaultMeetings, selectBestMeeting } from 'modules/meetings/lib/utils/scheduledMeetings'

import { MeetingsDefaultNavigation } from './components/MeetingsDefaultNavigation'
import { MeetingsNavigation } from './components/MeetingsNavigation'
import { MeetingsNavigationSkeleton } from './components/MeetingsNavigationSkeleton'

const EmployeeMeetingsPage = ({ children }: { children?: ReactNode }) => {
  const { employee } = useCurrentSession()
  const { employeeId, meetingId } = useParams<{ employeeId: string; meetingId: string }>()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [startDate, setStartDate] = useState<string | undefined>(undefined)
  const [endDate, setEndDate] = useState<string | undefined>(undefined)
  const isInMeeting =
    pathname.includes(navigation.engagement.one_on_ones.employeeId.meetingId.room.path) ||
    pathname.includes(navigation.engagement.one_on_ones.employeeId.meetingId.record.path)
  const [resetKey, setResetKey] = useState(0)

  const { data: meetingAttendees } = useQuery(OneOnOnesMeetingAttendeesDocument, {
    variables: {
      meetingId: Number(meetingId),
    },
    skip: !meetingId,
  })

  // Calculate default date range when no dates are selected
  const defaultDateRange = useMemo(() => {
    if (startDate && endDate) {
      return { startDate, endDate }
    }

    const threeMonthsAgo = moment().subtract(3, 'months').format('YYYY-MM-DD')
    const threeMonthsAhead = moment().add(3, 'months').format('YYYY-MM-DD')

    return { startDate: threeMonthsAgo, endDate: threeMonthsAhead }
  }, [startDate, endDate])

  const {
    data: scheduledMeetings,
    loading: scheduledMeetingsLoading,
    refetch: refetchScheduledMeetings,
  } = useQuery(OneOnOnesScheduledMeetingsDocument, {
    variables: {
      attendeeEmployeeId: Number(employeeId),
      startDate: defaultDateRange.startDate,
      endDate: defaultDateRange.endDate,
    },
    skip: isInMeeting || employee?.id === Number(employeeId) || !employeeId,
  })

  useEffect(() => {
    const attendeeEmployeeId =
      meetingAttendees?.meetings?.meetingsConnection?.nodes?.[0]?.attendees?.filter(
        (attendee) => attendee.employee?.id !== employee?.id
      )?.[0]?.employee?.id

    if (Number(employeeId) === employee?.id && meetingId && attendeeEmployeeId) {
      // if the pathname includes the prejoin path, navigate to the prejoin page
      if (pathname.includes(navigation.engagement.one_on_ones.employeeId.meetingId.prejoin.path)) {
        navigate(
          navigation.engagement.one_on_ones.employeeId.meetingId.prejoin.fullPath({
            meetingId: Number(meetingId),
            employeeId: Number(attendeeEmployeeId),
          })
        )
        // if the pathname includes the room path, navigate to the room page
      } else if (
        pathname.includes(navigation.engagement.one_on_ones.employeeId.meetingId.room.path)
      ) {
        navigate(
          navigation.engagement.one_on_ones.employeeId.meetingId.room.fullPath({
            meetingId: Number(meetingId),
            employeeId: Number(attendeeEmployeeId),
          })
        )
        // navigate to the meeting page
      } else {
        navigate(
          navigation.engagement.one_on_ones.employeeId.meetingId.fullPath({
            meetingId: Number(meetingId),
            employeeId: Number(attendeeEmployeeId),
          })
        )
      }
    }
  }, [
    employeeId,
    employee?.id,
    meetingId,
    navigate,
    navigation,
    meetingAttendees?.meetings?.meetingsConnection?.nodes,
    pathname,
  ])

  const scheduledMeetingsList = useMemo(
    () => scheduledMeetings?.meetings?.scheduledMeetingsConnection?.nodes ?? [],
    [scheduledMeetings?.meetings?.scheduledMeetingsConnection?.nodes]
  )

  useEffect(() => {
    if (!meetingId && scheduledMeetingsList.length > 0) {
      const bestMeeting = selectBestMeeting(scheduledMeetingsList)

      if (bestMeeting && bestMeeting.id) {
        navigate(
          navigation.engagement.one_on_ones.employeeId.meetingId.fullPath({
            meetingId: Number(bestMeeting.id),
            employeeId: Number(employeeId),
          })
        )
      }
    }
  }, [meetingId, scheduledMeetingsList, employeeId, navigate, navigation])

  const { upcomingMeetings, pastMeetings } = useMemo(
    () => getDefaultMeetings(scheduledMeetingsList),
    [scheduledMeetingsList]
  )

  if (isInMeeting) {
    return <Outlet />
  }

  return (
    <Box
      border={{
        top: {
          width: 's1',
          style: 'solid',
          color: 'grey200',
        },
      }}
      height="full"
      flexDirection="row"
    >
      <Box
        border={{
          right: {
            width: 's1',
            style: 'solid',
            color: 'grey200',
          },
        }}
        height="full"
        style={{
          width: '248px',
        }}
      >
        <Box
          paddingX="s12"
          paddingTop="s12"
          flexDirection="row"
          justifyContent="flexStart"
          alignItems="center"
          gap="s4"
        >
          <Box flexGrow>
            <OneDateNavigator
              key={resetKey}
              hideGoToCurrent
              granularities={['year']}
              defaultValue={{
                granularity: 'year',
                value: undefined,
              }}
              onSelect={(value) => {
                setStartDate(moment(value?.value?.from).format('YYYY-MM-DD'))
                setEndDate(moment(value?.value?.to).format('YYYY-MM-DD'))
              }}
            />
          </Box>

          {startDate && endDate && (
            <F0Button
              label={i18n.t('common.clear')}
              icon={Cross}
              onClick={() => {
                setStartDate(undefined)
                setEndDate(undefined)
                setResetKey((prev) => prev + 1)
              }}
              variant="ghost"
              hideLabel
            />
          )}
        </Box>
        {scheduledMeetingsLoading ? (
          <MeetingsNavigationSkeleton />
        ) : startDate && endDate ? (
          <MeetingsNavigation
            scheduledMeetings={scheduledMeetingsList}
            employeeId={Number(employeeId)}
            refetch={() => {
              refetchScheduledMeetings()
            }}
          />
        ) : (
          <MeetingsDefaultNavigation
            scheduledUpcomingMeetings={upcomingMeetings}
            scheduledPastMeetings={pastMeetings}
            employeeId={Number(employeeId)}
            refetch={() => {
              refetchScheduledMeetings()
            }}
          />
        )}
      </Box>
      <Box flexGrow position="relative" style={{ width: 'calc(100% - 248px)' }}>
        {meetingId ? (
          children || <Outlet />
        ) : scheduledMeetingsLoading ? (
          <Box height="full" width="full" alignItems="center" justifyContent="center">
            <Spinner />
          </Box>
        ) : (
          <Box padding="s12" width="full" height="full" alignItems="center" justifyContent="center">
            <OneEmptyState
              actions={[
                {
                  label: i18n.t(
                    'meetings.one_on_ones.employee_meeting_page.blank_state.button_new'
                  ),
                  onClick: () => {
                    const newSearchParams = new URLSearchParams(searchParams)
                    newSearchParams.set('schedule', 'true')
                    setSearchParams(newSearchParams)
                  },
                  variant: 'default',
                },
              ]}
              description={i18n.t(
                'meetings.one_on_ones.employee_meeting_page.blank_state.description'
              )}
              emoji="🗓️"
              title={i18n.t('meetings.one_on_ones.employee_meeting_page.blank_state.title')}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default EmployeeMeetingsPage
